import { useEffect, useMemo, useState } from "react";
import { useSearch } from "wouter";
import { Sentry } from "@/sentry";
import { Button } from "@/components/ui/button";
import { PublicLayout } from "@/components/layout/public-layout";
import { Alert, AlertDescription } from "@/components/ui/alert";

export default function SentryTestPage() {
  const search = useSearch();
  const secret = useMemo(() => new URLSearchParams(search).get("secret"), [search]);
  const [serverResult, setServerResult] = useState<string | null>(null);
  const [serverTestsEnabled, setServerTestsEnabled] = useState<boolean | null>(null);

  useEffect(() => {
    fetch("/api/health/sentry-test/enabled")
      .then((res) => res.json())
      .then((data: { enabled?: boolean }) => setServerTestsEnabled(Boolean(data.enabled)))
      .catch(() => setServerTestsEnabled(false));
  }, []);

  const triggerClientError = () => {
    throw new Error("HMS Sentry client test error (thrown)");
  };

  const triggerClientCapture = () => {
    Sentry.captureException(
      new Error("HMS Sentry client test error (captureException)"),
    );
    setServerResult("Client captureException called — check Sentry.");
  };

  const callServer = async (path: string) => {
    if (!serverTestsEnabled) {
      setServerResult(
        "Server tests are disabled. Set SENTRY_TEST_SECRET on the server (e.g. Railway env vars).",
      );
      return;
    }

    if (!secret) {
      setServerResult(
        "Add ?secret=... to this URL using the same value as SENTRY_TEST_SECRET on the server.",
      );
      return;
    }

    setServerResult("Sending...");
    const response = await fetch(`${path}?secret=${encodeURIComponent(secret)}`, {
      credentials: "include",
    });
    const body = await response.json().catch(() => ({}));
    setServerResult(
      response.ok
        ? (body.message as string) || "Request completed."
        : response.status === 404
          ? "HTTP 404: secret mismatch or SENTRY_TEST_SECRET not set on the server."
          : `HTTP ${response.status}: ${body.message || response.statusText}`,
    );
  };

  return (
    <PublicLayout maxWidth="md">
      <div className="space-y-6 py-8">
        <div>
          <h1 className="text-2xl font-semibold">Sentry test</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Verify client and server error reporting. Server tests require{" "}
            <code className="text-xs">SENTRY_TEST_SECRET</code> on the server and
            the same value in <code className="text-xs">?secret=...</code> in this
            URL.
          </p>
        </div>

        {serverTestsEnabled === false && (
          <Alert>
            <AlertDescription>
              Server smoke tests are not configured. Add{" "}
              <code className="text-xs">SENTRY_TEST_SECRET</code> to your Railway
              service variables, redeploy, then reload with{" "}
              <code className="text-xs">?secret=that-same-value</code>.
            </AlertDescription>
          </Alert>
        )}

        <div className="space-y-3">
          <h2 className="text-sm font-medium">Client</h2>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" onClick={triggerClientCapture}>
              Send client captureException
            </Button>
            <Button type="button" variant="destructive" onClick={triggerClientError}>
              Throw client error
            </Button>
          </div>
        </div>

        <div className="space-y-3">
          <h2 className="text-sm font-medium">Server</h2>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={serverTestsEnabled === false}
              onClick={() => callServer("/api/health/sentry-test")}
            >
              Send server captureException
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={serverTestsEnabled === false}
              onClick={() => callServer("/api/health/sentry-test/throw")}
            >
              Throw server error
            </Button>
          </div>
        </div>

        {serverResult && (
          <p className="text-sm text-muted-foreground">{serverResult}</p>
        )}
      </div>
    </PublicLayout>
  );
}
